import React, { useState, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl } from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// Screen Master: every link the side bar can show. Same shape as the other master lists -- search,
// a status filter defaulting to Active, and Edit / Disable / Enable / Delete per row.
function List() {

    const [screenList, setScreenList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    // Status defaults to Active, matching the other master lists. '' is All.
    const [statusFilter, setStatusFilter] = useState({ value: '1', label: 'Active' });

    // role 0 = superadmin, 1 = admin. Both may enable and disable; only the superadmin may delete.
    const role = String(localStorage.getItem('userRole'));
    // Which buttons this user gets is Role Master's call now, not a role number's -- see
    // comman/permissions.js. `role` is still read because it is what the delete and enable
    // endpoints are told to check against.
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');

    const navigate = useNavigate();

    // Multi-select: nothing chosen is every screen, and any combination is allowed -- including
    // "No Department" alongside real ones, which asks for the orphans and those departments in
    // one list.
    const [departmentFilter, setDepartmentFilter] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);

    useEffect(() => {
        getScreenList();
        getDepartments();
    }, []);

    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        const status = statusFilter?.value ?? '';
        const departments = (departmentFilter ?? []).map((option) => option.value);
        setFilteredList(screenList.filter((item) => {
            const matchesQuery = !query
                || (item.name ?? '').toLowerCase().includes(query)
                || (item.path ?? '').toLowerCase().includes(query)
                || (item.departments ?? '').toLowerCase().includes(query);
            const matchesStatus = status === '' || String(item.status) === status;
            // department_ids is the list endpoint's GROUP_CONCAT of the departments holding this
            // screen. Department 0 is one of them, so "No Department" needs no special case: a
            // screen nothing else holds carries 0 and matches it like any other.
            const ids = String(item.department_ids ?? '').split(',').filter((entry) => entry !== '');
            const matchesDepartment = departments.length === 0
                || departments.some((value) => ids.includes(value));
            return matchesQuery && matchesStatus && matchesDepartment;
        }));
    }, [searchQuery, statusFilter, departmentFilter, screenList]);

    const getScreenList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/screen_master/list`);
            setScreenList(response.data.data);
        } catch (error) {
            console.log(error);
        }
    }

    // The departments to filter by, from Department Master -- department 0 among them, which is
    // where a screen sits until a real department takes it.
    const getDepartments = async () => {
        try {
            const response = await axios.get(`${baseUrl}/department_master/list`);
            // "No Department" is department 0 and arrives with the rest -- see
            // backend/sql/no_department.sql. Pulled to the front rather than invented here: it is
            // what somebody opening this filter is most often after, and it has no place in the
            // departments' own sequence.
            const departments = (response.data?.data ?? []).map((department) => ({
                value: String(department.id),
                label: department.name,
            }));
            setDepartmentOptions([
                ...departments.filter((option) => option.value === '0'),
                ...departments.filter((option) => option.value !== '0'),
            ]);
        } catch (error) {
            console.log(error);
        }
    };

    const handelFilterChange = (e) => {
        const { value } = e.target;
        setSearchQuery(value);
    };

    const handleClear = () => {
        setSearchQuery('');
        setStatusFilter({ value: '1', label: 'Active' });
        setDepartmentFilter([]);
    };

    const deleteScreen = async (id) => {
        const shouldDelete = window.confirm("Are you sure you want to disable this screen? It will drop out of the side bar.");
        if (shouldDelete) {
            try {
                await axios.delete(`${baseUrl}/screen_master/delete/${id}`, { data: { role } });
                window.location.reload();
            } catch (error) {
                alert(error?.response?.data?.msg ?? 'Could not disable the screen.');
            }
        }
    };

    const enableScreen = async (id) => {
        if (!window.confirm("Enable this screen?")) return;
        try {
            await axios.post(`${baseUrl}/screen_master/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the screen.');
        }
    };

    // Delete removes the row, unlike Disable which is recoverable, so its confirmation says so.
    // It also unhooks the screen from every department it sits under.
    const destroyScreen = async (id) => {
        if (!window.confirm("Permanently DELETE this screen? Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/screen_master/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the screen.');
        }
    };

    const editScreen = async (id) => {
        navigate(`/screen_master/edit/${btoa(id)}`, { state: screenList.filter(item => item.id == id) });
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Screen</h1>
                <div>
                    <Link to="/screen_master/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Screen</button>
                    </Link>
                </div>
            </div>
            <div className="relative shadow-md sm:rounded-lg">
                <div className="pb-4 flex flex-column sm:flex-row flex-wrap space-y-4 sm:space-y-0 items-center justify-between">
                    <label htmlFor="table-search" className="sr-only">Search</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 rtl:inset-r-0 rtl:right-0 flex items-center ps-3 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path></svg>
                        </div>
                        <input type="text" value={searchQuery} onChange={handelFilterChange} id="table-search" className="block p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Search for screens" />
                    </div>
                    <div className="flex items-center gap-4 ms-4 me-auto">
                        <Select
                            {...menuAboveTable}
                            isMulti
                            classNamePrefix="select"
                            className="basic-multi-select min-w-56"
                            value={departmentFilter}
                            name="department"
                            onChange={(selected) => setDepartmentFilter(selected ?? [])}
                            options={departmentOptions}
                            placeholder="All departments"
                        />
                        <Select
                            {...menuAboveTable}
                            classNamePrefix="select"
                            className="basic-multi-select w-36"
                            value={statusFilter}
                            name="status"
                            onChange={(selected) => setStatusFilter(selected)}
                            options={[
                                { value: '', label: 'All' },
                                { value: '1', label: 'Active' },
                                { value: '0', label: 'Inactive' }
                            ]}
                            placeholder="Status"
                            isClearable={false}
                        />
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                        >
                            Clear
                        </button>
                    </div>
                </div>
                {/* The header stays put while the rows scroll under it. The height cap is what
                    makes that work -- a container with no limit never scrolls, and a sticky
                    header has nothing to hold itself against. */}
                <div className="overflow-auto max-h-[65vh]">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Screen Name</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Path</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Menu Group</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Department</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Status</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.map((item) => (
                            <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.name}</th>
                                <td className="px-6 py-4">{item.path}</td>
                                {/* The heading it appears under, and separately the departments
                                    that grant it. The two are different questions now. */}
                                <td className="px-6 py-4">{item.menu_group_name ?? '-'}</td>
                                <td className="px-6 py-4">{item.departments ?? '-'}</td>
                                <td className="px-6 py-4">{item.status == 1 ? 'Active' : 'Inactive'}</td>
                                <td className="px-6 py-4">
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => editScreen(item.id)}
                                            className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                            Edit
                                        </button>
                                        {item.status == 1 && canDisable && <button
                                            type="button"
                                            onClick={() => deleteScreen(item.id)}
                                            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                            Disable
                                        </button>}
                                        {item.status == 0 && canActivate && <button
                                            type="button"
                                            onClick={() => enableScreen(item.id)}
                                            className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                            Enable
                                        </button>}
                                        {canDelete && <button
                                            type="button"
                                            onClick={() => destroyScreen(item.id)}
                                            className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                            Delete
                                        </button>}
                                    </div>
                                </td>
                            </tr>))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

export default List;
